/**
 * Die Glocke — aus dem Bestand statt aus dem Quelltext.
 *
 * `entwurf/huelle.ts` führte bis zum 25.08.2026 **vier erfundene Meldungen**:
 *
 *     'Mira Engler beantragt SAP ERP · Adminrecht für sich selbst.'
 *     'Hanno Gerste vereint Bestellung anlegen und Rechnung freigeben.'
 *
 * Erfundene Personennamen — in einem Mandanten, der ausdrücklich **keine
 * Personen führt**. Dazu Zeitangaben („vor 8 Min"), die seit dem ersten Start
 * dieselben waren, und eine Kampagne mit Enddatum, die es nicht gibt. Es ist
 * dieselbe Klasse wie das erfundene Audit-Log vom 13.08.2026 und die
 * „309 Rollen" in der Navigation, nur an der Stelle, an der man sie am
 * ehesten für bare Münze nimmt: eine Glocke kündigt an, dass etwas passiert
 * ist.
 *
 * ## Was jetzt darin steht
 *
 * Drei Mengen, jede als **serverseitiger Zähler** (`gesamt` bei `groesse: 1`)
 * — nicht als Zählung über eine geladene Seite:
 *
 * | Meldung | Quelle | Ziel |
 * |---|---|---|
 * | Änderungsfreigaben | `/api/freigaben?zustand=eingereicht` | `/genehmigungen` |
 * | Zugänge in der Warteliste | `/api/konten?ohneRolle=ja` | `/konten?warten=ja` |
 * | Eigene Rezertifizierungen | `…/positionen?zustand=offen&meine=ja` | `/rezertifizierung` |
 *
 * **SoD steht bewusst nicht dabei.** `/api/sod/befunde` blättert nicht und
 * überträgt jede Zeile, um eine Zahl zu bilden; das ist an der Startseite
 * vertretbar und an einer Glocke nicht, die auf **jeder** Seite mitlädt.
 * Vermerkt statt verschwiegen — die Startkachel zeigt die Zahl weiterhin.
 *
 * ## Keine Meldung ist eine Auskunft
 *
 * Ist nichts offen, sagt das Menü das. Eine Glocke, die dann eine erfundene
 * Zeile zeigt, damit sie nicht leer aussieht, ist schlimmer als eine leere.
 * Und solange die Zähler laufen, steht dort **keine Null** — dieselbe Regel
 * wie bei den Aufgabenkacheln.
 */

import { useMemo } from 'react';
import { useListe } from './haken.js';
import { usePositionen } from './rezertifizierung.js';
import { zaehlerGefragt } from './aufgabenzaehler.js';
import type { Profil } from '../entwurf/navigation.js';

export interface Bestandsmeldung {
  titel: string;
  text: string;
  /** Schlüssel in `IC`. */
  icon: string;
  farbe: string;
  href: string;
  zahl: number;
}

export interface Meldungsstand {
  meldungen: Bestandsmeldung[];
  laedt: boolean;
  /** Mindestens ein Zähler hat nicht geantwortet. */
  fehler: boolean;
  /** Kein Zähler ist überhaupt gefragt — Module aus oder Rechte fehlen. */
  aus: boolean;
}

const LEER: Bestandsmeldung[] = [];

export function useMeldungen(
  mandant: string,
  profil: Profil,
  /** Die echten Rechte aus `/api/ich` — nicht die Persona des Browsers. */
  darfDas: (recht: string | null) => boolean,
): Meldungsstand {
  const freigabenAn = zaehlerGefragt('freigaben', profil, darfDas);
  const kontenAn = zaehlerGefragt('kontenWarten', profil, darfDas);
  const rezertAn = zaehlerGefragt('rezert', profil, darfDas);

  const freigaben = useListe<unknown>('freigaben', mandant, {
    seite: 1, groesse: 1, filter: { zustand: 'eingereicht' }, aus: !freigabenAn,
  });
  const konten = useListe<unknown>('konten', mandant, {
    seite: 1, groesse: 1, filter: { ohneRolle: 'ja' }, aus: !kontenAn,
  });
  const rezert = usePositionen(mandant, {
    zustand: 'offen', meine: true, seite: 1, groesse: 1, aus: !rezertAn,
  });

  return useMemo(() => {
    const meldungen: Bestandsmeldung[] = [];

    const freigabenZahl = freigabenAn ? freigaben.daten?.gesamt : undefined;
    if (freigabenZahl) {
      meldungen.push({
        titel: freigabenZahl === 1 ? 'Eine Änderung wartet auf Freigabe'
          : freigabenZahl + ' Änderungen warten auf Freigabe',
        text: 'Eingereicht und noch nicht entschieden. Wer eingereicht hat, '
          + 'entscheidet nicht selbst.',
        icon: 'haken', farbe: '--warning', href: '/genehmigungen', zahl: freigabenZahl,
      });
    }

    const kontenZahl = kontenAn ? konten.daten?.gesamt : undefined;
    if (kontenZahl) {
      meldungen.push({
        titel: kontenZahl === 1 ? 'Ein Zugang wartet auf Freischaltung'
          : kontenZahl + ' Zugänge warten auf Freischaltung',
        text: 'Angemeldet, aber ohne Anwendungsrolle — und damit ohne Sicht auf '
          + 'irgendetwas.',
        icon: 'personen', farbe: '--brand-primary', href: '/konten?warten=ja', zahl: kontenZahl,
      });
    }

    const rezertZahl = rezertAn ? rezert.daten?.gesamt : undefined;
    if (rezertZahl) {
      meldungen.push({
        titel: rezertZahl === 1 ? 'Eine Rezertifizierung liegt bei Ihnen'
          : rezertZahl + ' Rezertifizierungen liegen bei Ihnen',
        text: 'Offene Positionen aus laufenden Kampagnen. Nichtentscheidung gilt '
          + 'nicht als Bestätigung.',
        icon: 'kreis', farbe: '--warning', href: '/rezertifizierung', zahl: rezertZahl,
      });
    }

    const abrufe = [
      freigabenAn ? freigaben : null,
      kontenAn ? konten : null,
      rezertAn ? rezert : null,
    ].filter((x): x is NonNullable<typeof x> => x !== null);

    return {
      meldungen: meldungen.length > 0 ? meldungen : LEER,
      laedt: abrufe.some((a) => a.laedt),
      fehler: abrufe.some((a) => a.fehler !== null),
      aus: abrufe.length === 0,
    };
  }, [
    freigabenAn, freigaben.daten, freigaben.fehler, freigaben.laedt,
    kontenAn, konten.daten, konten.fehler, konten.laedt,
    rezertAn, rezert.daten, rezert.fehler, rezert.laedt,
  ]);
}
